import React, {memo, useEffect} from 'react'
import _ from 'lodash'
import {Accordion, Dropdown, Form, Grid, Loader} from 'semantic-ui-react'
import Highlighter from 'react-highlight-words'
import FlexSearch from 'flexsearch'
import {fromISOString, qs, toLocaleString} from '../../../../../../util'
import {
  withEnvironmentData,
  withHelmet,
  withPropsPresent,
  withQueryParams,
  withResourceData,
  withServiceData,
  withServicesData,
  withVersionData
} from '../../../../../../hoc'
import {useSafeState} from '../../../../../../hook'

const _Logs = (
  {
    service,
    version,
    environment,
    resource,
    logs = [],
    queryText = '',
    logId,
    responseSelections = [],
    index,
    navigate,
    basePath = '/c/services/:serviceId/versions/:versionId/environments/:environmentId/resources/:resourceId/logs'
  }
) => {
  const log = logs.find(l => l.id === logId)
  const activeIndex = logs.indexOf(log)
  const [state, setState] = useSafeState({
    searching: false,
    activeIndex,
    queryText,
    matches: logs
  })

  const responseOptions = _.orderBy(logs.reduce((options, log) => {
    const responseStatus = log.res.status
    const responseName = log.res.name
    const responseContentType = log.res.headers['content-type'] || log.res.headers['Content-Type'] || ''
    const responseKeyParts = []
    if (log.res.type !== 'proxy') {
      responseKeyParts.push(responseStatus)
      if (responseContentType) {
        responseKeyParts.push(responseContentType)
      }
      responseKeyParts.push(responseName)
    } else {
      responseKeyParts.push('proxied')
      responseKeyParts.push(responseName)
    }
    const responseKey = responseKeyParts.join(' - ')
    if (!options.some((o) => o.key === responseKey)) {
      options.push({
        key: responseKey,
        text: responseKey,
        value: log.responseId
      })
    }
    return options
  }, []), ['key'], ['asc'])

  const getPathPrefix = () => {
    return basePath
      .replace(':serviceId', service.id)
      .replace(':versionId', version.id)
      .replace(':environmentId', environment.id)
      .replace(':resourceId', resource.id)
  }

  useEffect(() => {
    search(queryText)
  }, [queryText, responseSelections, logId])

  const doNav = (queryText, logId, responseSelections = []) => {
    const query = {}
    if (queryText) {
      query.log_query_text = queryText
    }
    if (logId) {
      query.log_id = logId
    }
    if (responseSelections.length) {
      query.response_selections = responseSelections.join(',')
    }
    navigate(`${getPathPrefix()}${qs(query)}`)
  }

  const search = (queryText) => {
    setState({...state, queryText, searching: true})
    let matches = logs
    let {activeIndex} = state
    if (queryText) {
      matches = index.search(queryText).map(r => logs.find(l => l.id === r))
    }
    if (responseSelections.length) {
      matches = matches.filter(l => responseSelections.includes(l.responseId))
    }
    if (logId) {
      const log = matches.find(l => l.id === logId)
      if (log) {
        activeIndex = matches.indexOf(log)
      } else {
        activeIndex = -1
      }
    } else {
      activeIndex = -1
    }

    setState({
      ...state,
      queryText,
      responseSelections,
      searching: false,
      activeIndex,
      matches
    })
  }

  const filter = (responseSelections) => {
    setState({
      ...state,
      responseSelections,
      matches: responseSelections.length ? logs.filter(l => responseSelections.includes(l.responseId)) : logs
    })
  }

  const onMatchSelected = (e, titleProps) => {
    const {index} = titleProps
    let {activeIndex} = state
    activeIndex = activeIndex === index ? -1 : index
    setState({...state, activeIndex})
    doNav(state.queryText, activeIndex > -1 ? state.matches[activeIndex].id : undefined, responseSelections)
  }

  const onQueryChange = (e) => {
    const {value} = e.target
    search(value)
    doNav(value, logId, responseSelections)
  }

  const onResponseSelectionChange = (e, d) => {
    filter(d.value)
    doNav(state.queryText, logId, d.value)
  }

  const panels = state.matches.map((log, i) => {
    return {
      key: `${log.id}`,
      title: {
        key: `title-${log.id}`,
        content: (
          <span data-test='request-log-created-at'>
            <b>{log.createdAt}</b>
          </span>
        )
      },
      content: {
        key: `content-${log.id}`,
        content: getContent(state.activeIndex, i, log, state.queryText)
      }
    }
  })

  return (
    <div data-test='request-logs'>
      <Grid>
        <Grid.Row>
          <Grid.Column width='8'>
            <Form>
              <Form.Field>
                <Form.Input placeholder='Query log data' name='query' value={state.queryText} icon='search'
                            autoComplete='off'
                            data-test='request-logs-query-textbox'
                            onChange={onQueryChange}/>
              </Form.Field>
            </Form>
          </Grid.Column>
          <Grid.Column width='8'>
            <Dropdown placeholder='Filter by matching response(s)' fluid multiple search selection
                      options={responseOptions}
                      value={responseSelections}
                      data-test='request-logs-responses-dropdown'
                      onChange={onResponseSelectionChange}/>
          </Grid.Column>
        </Grid.Row>
        <Grid.Row>
          <Grid.Column width='16'>
            {
              state.searching && <Loader active inline='centered' style={{marginTop: '15px', marginBottom: '15px'}}/>
            }
            {
              !state.searching && state.matches.length > 0 && (
                <Accordion fluid styled activeIndex={state.activeIndex} panels={panels}
                           onTitleClick={onMatchSelected}/>
              )
            }
            {
              !state.searching && state.matches.length === 0 && (
                <div>
                  No logs found
                </div>
              )
            }
          </Grid.Column>
        </Grid.Row>
      </Grid>
    </div>
  )
}

const getContent = (activeIndex, iteratorIndex, log, query = '') => {
  if (activeIndex === iteratorIndex) {
    return (
      <div data-test='request-log-text'>
<pre style={{'whiteSpace': 'pre-wrap', 'wordWrap': 'break-word'}}>
<Highlighter
  searchWords={query.split(' ').map((w) => w.replace(new RegExp('"', 'g'), ''))}
  autoEscape={true}
  textToHighlight={log.text}
/>
</pre>
      </div>
    )
  }
  return null
}


let Logs = (props) => {
  let {resource, query} = props
  let {logs = []} = resource
  const queryText = query.log_query_text
  const logId = query.log_id
  const responseSelections = query.response_selections ? _.isArray(query.response_selections) ? query.response_selections : query.response_selections.split(',') : []
  const index = FlexSearch.create()
  logs = logs.map(rl => {
    return {
      ...rl,
      responseId: rl.responseId || `${rl.res.name}-${rl.res.status}`,
      text: JSON.stringify(_.pick(rl, ['req', 'res', 'proxyExchange']), null, 2),
      createdAt: toLocaleString(fromISOString(rl.createdAt))
    }
  })
  logs.forEach(l => index.add(l.id, l.text))
  props = {...props, queryText, logId, index, logs, responseSelections}
  return (<_Logs {...props}/>)
}

Logs = withPropsPresent(Logs, ['services', 'service', 'version', 'environment', 'resource'])
Logs = withResourceData(Logs, 'Loading resource...')
Logs = withEnvironmentData(Logs, 'Loading environment...')
Logs = withVersionData(Logs, 'Loading version...')
Logs = withServiceData(Logs, 'Loading service...')
Logs = withServicesData(Logs, 'Loading services...')
Logs = withQueryParams(Logs)
Logs = withHelmet(Logs)
Logs = memo(Logs)

export {_Logs, Logs}

export default Logs
